import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { AiOutlineArrowRight } from 'react-icons/ai';
import { RootState } from '../../../../store/store';
import { FlightView } from '../../../../interfaces/flightInterface';
import { CabinOption, CABIN_LABEL_KEYS } from '@skygroup/shared/utils/cabinClass';
import { modifyFlightData } from '../../../SearchDatePicker/widgets/SearchResults/FlightCard2';
import {
  formatMinutes,
  getLegDurationMinutes,
} from '@skygroup/shared/utils/flightDuration';
import {
  changesBeforeLeg,
  changesWithinLeg,
  findAirportChanges,
} from '@skygroup/shared/utils/multiCityAirportChange';
import AirportChangeNotice from '../../../../components/AirportChangeNotice/AirportChangeNotice';
import './shoppingCartMultiCityLegs.less';

interface Props {
  /**
   * One entry per leg, each holding that leg's segments — the shape the backend
   * emits on `flightsInfo.legs` and the cart stores verbatim in `product_data`.
   */
  legs: FlightView[][];
}

/**
 * The multi-city itinerary as a CART line item: one compact row per leg.
 *
 * Why this exists rather than reusing MultiCityOfferCard, which also renders N
 * legs: that is a search-RESULT card. It carries a route header with arrows, a
 * per-leg airline logo with a hover tooltip, a full FlightOutLineSection per leg
 * (baggage icons, fare detail, and the `useGetFlightAdditionalFares` network call
 * behind `isDynamicFlight`), AgentCommissionSearch, an inventory badge, decorative
 * plane connectors and a price footer. All of that is right when a user is
 * comparing offers and wrong when they are reviewing a cart — the cart already
 * shows the route, pax and price in its own shell, so repeating them inside the
 * card is duplication, and the fare/baggage chrome is noise at three to five legs
 * tall.
 *
 * The leg collapse itself is NOT re-derived here: `modifyFlightData` is the same
 * helper the results card and FlightCard2 use, called ONCE PER LEG (never over
 * the flattened array), which is what keeps each row's stop count and endpoints
 * scoped to its own leg rather than spanning the whole trip.
 */
const ShoppingCartMultiCityLegs = ({ legs }: Props) => {
  const { searchDatePicker: t, general: general_t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const { siteConfig } = useSelector((data: RootState) => data.config.config);

  const legRows = useMemo(
    () =>
      (legs ?? [])
        // Index captured BEFORE the filter below. `findAirportChanges` anchors
        // every change to a position in the original `legs` array, so a row that
        // has been renumbered by dropping an empty leg would look up the wrong
        // leg's notices.
        .map((leg, legIndex) => ({ leg, legIndex }))
        // A leg with no segments cannot be described. The backend drops such
        // offers at the source, so this is belt-and-braces against a
        // partially-written product_data rather than an expected state.
        .filter(({ leg }) => leg?.length)
        .map(({ leg, legIndex }) => ({
          legIndex,
          // The collapsed leg: endpoints, dates, stop count, joint flight name.
          data: modifyFlightData(leg),
          // Timezone-safe leg total (shared/utils/flightDuration.ts); null when
          // the supplier gave no durations — the row then shows stops alone.
          durationMinutes: getLegDurationMinutes(leg),
          // The airline image/name live on the raw segment — modifyFlightData
          // only surfaces the OPERATING carrier when it differs from the
          // marketing one, which is not what we want to label the row with.
          airline: leg[0]?.airline,
        }))
        // modifyFlightData returns undefined for input it cannot collapse, so
        // narrow here rather than optional-chaining every field at the render
        // site — a row with no collapsed data has nothing to show anyway.
        .filter(
          (
            row
          ): row is {
            legIndex: number;
            data: NonNullable<typeof row.data>;
            durationMinutes: number | null;
            airline: any;
          } => Boolean(row.data)
        ),
    [legs]
  );

  /**
   * Same detection the search card runs, so an itinerary that warned about a
   * change of airport while the traveller was choosing still warns about it at
   * the moment they pay. The cart is the last surface before checkout, which is
   * exactly where a silent Gatwick-in / Heathrow-out becomes expensive.
   */
  const airportChanges = useMemo(() => findAirportChanges(legs), [legs]);

  if (!legRows.length) {
    return null;
  }

  /**
   * Matches the platform's existing stop wording exactly (see
   * FlightOutLineSection): a dedicated singular key, then "N stops".
   */
  const stopsLabel = (numOfStops: number) => {
    if (numOfStops === 0) return t.directFlight;
    return numOfStops === 1 ? t.stop_1 : `${numOfStops} ${t.stops}`;
  };

  /**
   * `.utc()`, like every other flight-time renderer on the platform.
   *
   * A flight timestamp is the wall-clock time AT THE AIRPORT, encoded as though
   * it were UTC. Rendering it in the viewer's timezone therefore shifts it by
   * whatever offset that viewer happens to be in — a TLV departure at 01:00
   * printed 05:00 for a GMT+4 operator — and the cart disagreed with the search
   * card and the info page for the very same flight.
   *
   * The rest of the platform already does this: `FlightOutLineSection` (search
   * results, info page, and MultiCityOfferCard) builds `moment(...).utc()`, the
   * itinerary template uses the `dateFormatUtc` helper, and the backoffice
   * order-products table uses `moment(...).utc()`. This component was the only
   * cart product rendering a flight TIME, so it was the only place the drift
   * was visible — the bundle cart shows a date alone, which hid the same bug.
   *
   * Tenant date format, not a hand-picked one. A weekday would read better for a
   * multi-leg trip, but moment's locale is not configured per tenant here, so it
   * would print English weekday names inside a Hebrew or Arabic cart.
   */
  const formatDate = (unixSeconds: number) =>
    moment(unixSeconds * 1000)
      .utc()
      .format(siteConfig?.dateFormat ?? 'DD/MM/YYYY');

  const formatTime = (unixSeconds: number) =>
    moment(unixSeconds * 1000)
      .utc()
      .format('HH:mm');

  return (
    <div className="mc-cart-legs" data-testid="multi-city-cart-legs">
      {legRows.map(({ data: leg, airline, legIndex, durationMinutes }, index: number) => (
        <React.Fragment
          key={`${leg.from?.code}-${leg.to?.code}-${leg.departure_date}-${index}`}
        >
          {/* Above the row it precedes, so the itinerary reads top-to-bottom in
              travel order: land, change airport, take off again. Outside
              `.mc-cart-leg` deliberately — that element carries the row's own
              hairline separator and flex geometry. */}
          {changesBeforeLeg(airportChanges, legIndex).map((change) => (
            <AirportChangeNotice
              key={`before-${legIndex}-${change.arrivalCode}-${change.departureCode}`}
              change={change}
              compact
            />
          ))}
          <div className="mc-cart-leg" data-testid="multi-city-cart-leg">
            <span className="mc-cart-leg-index">{index + 1}</span>

            <div className="mc-cart-leg-body">
              <div className="mc-cart-leg-meta">
                {airline?.image ? (
                  <img
                    className="mc-cart-leg-logo"
                    src={airline.image}
                    alt={airline.name ?? ''}
                    loading="lazy"
                  />
                ) : null}
                {/* Carrier + flight number in the codebase's own idiom; for a
                    connecting leg modifyFlightData joins both segments' names. */}
                <span className="mc-cart-leg-flight">{leg.flight_joint_name}</span>
                <span className="mc-cart-leg-date">
                  {formatDate(leg.departure_date)}
                </span>
              </div>

              <div className="mc-cart-leg-route">
                <span className="mc-cart-leg-endpoint">
                  <b>{leg.from?.code}</b> {formatTime(leg.departure_date)}
                </span>
                <AiOutlineArrowRight className="mc-cart-leg-arrow" />
                <span className="mc-cart-leg-endpoint">
                  <b>{leg.to?.code}</b> {formatTime(leg.arrival_date)}
                </span>
                <span
                  className={`mc-cart-leg-stops${
                    leg.num_of_stops === 0 ? ' is-direct' : ''
                  }`}
                >
                  {[
                    formatMinutes(durationMinutes, general_t?.h, general_t?.min),
                    stopsLabel(leg.num_of_stops),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {/*
                  The DELIVERED cabin, per leg. SKY-2306 lets a user pick a cabin
                  and the results card shows what each leg actually came back as —
                  but the cart and checkout dropped it, so the one attribute the
                  user deliberately chose was invisible at the moment of purchase.
                  A round trip in the same cart shows it (via FlightCard2), so this
                  was also inconsistent between products.

                  Same source and idiom as FlightOutLineSection: modifyFlightData's
                  per-leg collapse, with `cabinMixed` shown rather than silently
                  claiming one cabin for a leg whose segments disagree (Amadeus
                  multi-city filters with coverage: 'MOST_SEGMENTS', so a mixed leg
                  is legitimate, not a data fault).
                */}
                {leg.cabin && (
                  <span className="mc-cart-leg-cabin">
                    {t[CABIN_LABEL_KEYS[leg.cabin as CabinOption]]}
                    {leg.cabinMixed && <> · {t.mixedCabin}</>}
                  </span>
                )}
              </div>

              {/* A self-transfer at a connection INSIDE this leg. Inside the body
                  rather than above the row, because the row's own route line
                  reports the leg as first-`from` to last-`to` and this is the
                  correction to it. */}
              {changesWithinLeg(airportChanges, legIndex).map((change) => (
                <AirportChangeNotice
                  // See MultiCityOfferCard: the airport pair repeats on a leg
                  // that revisits the same two airports, so keying on it gives
                  // two children the same key and React can no longer tell them
                  // apart across an update. boardingIndex is the connection's
                  // own position within the leg, so it is unique.
                  key={`within-${legIndex}-${change.boardingIndex}`}
                  change={change}
                  compact
                />
              ))}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default ShoppingCartMultiCityLegs;
